 
import { action, runInAction, observable } from 'mobx';
import { ErrorInterface } from 'modules/core/api/globalApi';
import controller from './controller';
import {
  RoleAndUserPermissionInterface,
  CreatePermissionInterface,
  UpdatePermissionInterface,
  UpdatePermissionInListRequest,
} from '../api/permissionsAPI';
export interface PermissionStore {
  rolePermissions: RoleAndUserPermissionInterface[],
  rolePermissionsById: number,
  userPermissionsById: string,
  error: ErrorInterface,
}
import pick from 'lodash/pick';

const initialValues: PermissionStore = {
  // для больших массивов конструкция. В теории, может быть полезна тут:
  rolePermissions: observable.array<RoleAndUserPermissionInterface>([]), // Более эффективно
  rolePermissionsById: null as number,
  userPermissionsById: null as string,
  error: null as ErrorInterface,
};

export const permissionStore = observable(initialValues, {
  // Опционально: настройка глубины наблюдения
  rolePermissions: observable.deep,
});

export const permissionActions = {

  async getPermissionRole(roleId: number): Promise<void> {
    permissionActions.clearError();
    runInAction(() => {
      permissionStore.rolePermissionsById = roleId;
    });
  
    const response = await controller.getPermissionRole({ roleId });
    runInAction(() => {
      permissionActions.handleError(response);
      if (response.errorData) {
        permissionStore.rolePermissionsById = null;
      } else {
        permissionStore.rolePermissions = response.data;
      }
    });
  },

  async getPermissionUser(userId: string): Promise<void> {
    permissionActions.clearError();
    runInAction(() => {
      permissionStore.userPermissionsById = userId;
    });
  
    const response = await controller.getPermissionUser({ userId });
    runInAction(() => {
      permissionActions.handleError(response);
      if (response.errorData) {
        permissionStore.userPermissionsById = null;
      } else {
        permissionStore.rolePermissions = response.data;
      }
    });
  },

  updatePermissionInList: (request: UpdatePermissionInListRequest): void => {
    runInAction(() => {
      const item = permissionStore.rolePermissions.find(
        (v) => v.operationId === request.operationId
      );
      if (item) {
        item[request.permissionType] = !item[request.permissionType];
        item.updated = true;
      }
    });
  }, 

  savePermissions: async (): Promise<void> => {
    const roleId = permissionStore.rolePermissionsById;
    const userId = permissionStore.userPermissionsById;

    const updatePermissionArray = permissionStore.rolePermissions
      .filter(permission => permission.updated && permission.id > 0)
      .map(v => { return { ...v, id: v.id } });

    const createdPermissionArray = permissionStore.rolePermissions
      .filter(permission => permission.updated && (permission.id ?? 0) < 1)
      .map(v => { return { ...v, roleId: roleId ?? undefined, userId: userId ?? undefined } });   

    const promises = [
      createdPermissionArray.length && permissionActions.createPermission(createdPermissionArray),
      updatePermissionArray.length && permissionActions.updatePermission(updatePermissionArray),
    ].flatMap(promise => promise || []);
  
    if (promises.length > 0) {
      await Promise.all(promises);
    }

    permissionActions.clearUpdatedFlags();
  },

  setRowPermission: (operationId: number, value: boolean): void => {
    runInAction(() => {
      const item = permissionStore.rolePermissions.find(
        (v) => v.operationId === operationId
      );
      if (item) {
        if ( !item.availableUneditable) { item.available = value; };
        if (item.operationType === 'EXTENDED') {
          if ( !item.permissionInsertUneditable) { item.permissionInsert = value; };
          if ( !item.permissionUpdateUneditable) { item.permissionUpdate = value; };
          if ( !item.permissionDeleteUneditable) { item.permissionDelete = value; };
          if (!value) { // для OldDate только сброс значения допускаем
            if ( !item.permissionEditOldDateUneditable) { item.permissionEditOldDate = value; };
          }
        }
        item.updated = true;
      }
    });
  },

  createPermission: async (request: CreatePermissionInterface[]): Promise<void> => {
    permissionActions.clearError();

    const clearedRequest = request.map(v => pick(v, [
      'operationId', 
      'available',
      'permissionInsert', 
      'permissionUpdate', 
      'permissionDelete', 
      'permissionEditOldDate',
      'availableUneditable',
      'permissionDeleteUneditable',
      'permissionEditOldDateUneditable',
      'permissionInsertUneditable',
      'permissionUpdateUneditable',
      'roleId',
      'userId',
    ]));

    const response = await controller.createPermission(clearedRequest);
    permissionActions.handleError(response);    
    if (!response.errorData) {
      runInAction(() => {
        // Далее по каждому объекту из ответа фиксируем id в rolePermissions, чтобы потом можно было PUT запросы кидать
        response.data?.forEach((k) => {
          const item = permissionStore.rolePermissions.find(
            (v) => v.operationId === k.operationId
          );
          if (item) {
            item.id = k?.id;
          }
        });
      });
    }
  },

  updatePermission: async (request: UpdatePermissionInterface[]): Promise<void> => {
    permissionActions.clearError();
    const clearedRequest = request.map(v => pick(v, [
      'id', 
      'available',
      'permissionInsert', 
      'permissionUpdate', 
      'permissionDelete', 
      'permissionEditOldDate',
      'availableUneditable',
      'permissionDeleteUneditable',
      'permissionEditOldDateUneditable',
      'permissionInsertUneditable',
      'permissionUpdateUneditable',
    ]));
    const response = await controller.updatePermission(clearedRequest);
    permissionActions.handleError(response);
  },

  clearUpdatedFlags: action((): void => {
    permissionStore.rolePermissions.forEach(item => {
      if (item.updated) {
        item.updated = false;
      }
    });
  }),

  clearPermissionRole: action((): void => {
    permissionStore.rolePermissions = [];
    permissionStore.rolePermissionsById = null;
    permissionStore.userPermissionsById = null;
  }),

  handleError: action((response, id:string = null) => {
    if (response.errorData) {
      const { errorData } = response;
      permissionStore.error = {
        message: errorData?.message,
        errorCode: errorData?.errorCode,
        timestamp: errorData?.timestamp,
        status: response?.status,
        connectError: response?.connectError,
        isError: response?.error,
        id,
      };
      throw errorData;
    }
  }),

  handleErrorWithoutThrow: action((response) => {
    if (response.errorData) {
      const { errorData } = response;
      permissionStore.error = errorData;
    }
  }),

  clearError: action(() => {
    permissionStore.error = null;
  }),

};
